import type { HTMLAttributes, KeyboardEvent, MouseEvent, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { ChevronUpIcon, ChevronDownIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';

// Canonical data table — glass card wrapper, muted uppercase header row,
// hairline row separators. Compose with OptionChip / Badge in cells.

export function Table({ className = '', children, ...rest }: HTMLAttributes<HTMLTableElement>): JSX.Element {
  return (
    <div className="rounded-hairline border border-border bg-card overflow-x-auto">
      <table className={`w-full text-left text-sm ${className}`} {...rest}>
        {children}
      </table>
    </div>
  );
}

export function THead({ children }: { children: React.ReactNode }): JSX.Element {
  return <thead className="border-b border-border/60">{children}</thead>;
}

export function TBody({ children }: { children: React.ReactNode }): JSX.Element {
  return <tbody className="divide-y divide-border/40">{children}</tbody>;
}

export interface TRProps extends HTMLAttributes<HTMLTableRowElement> {
  onClick?: (event: MouseEvent<HTMLTableRowElement>) => void;
}

export function TR({ className = '', children, onClick, onKeyDown, ...rest }: TRProps): JSX.Element {
  const interactive = Boolean(onClick);
  return (
    <tr
      className={`transition hover:bg-muted/50 ${interactive ? 'focus-ring cursor-pointer' : ''} ${className}`}
      onClick={onClick}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={(e: KeyboardEvent<HTMLTableRowElement>) => {
        onKeyDown?.(e);
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.(e as unknown as MouseEvent<HTMLTableRowElement>);
        }
      }}
      {...rest}
    >
      {children}
    </tr>
  );
}

// 4-100 · overflow strategy — chosen and argued here (not both speculative
// shapes built): the browser's default (auto) table-layout algorithm sizes
// every column to its WIDEST cell, so one long identity-column value (a
// name, an email) silently forces the whole table wide and `Table`'s own
// `overflow-x-auto` wrapper then scrolls the ENTIRE table just to reveal
// short columns (status, actions) that would otherwise fit. `truncate`
// (Tailwind's `overflow:hidden;text-overflow:ellipsis;white-space:nowrap`
// bundle) needs a bounded width to truncate AGAINST — a `max-w-*` ceiling —
// so the two "options" this story named are really one mechanism, not a
// choice: `max-w-xs` (the same cap already used ad hoc at this exact job in
// `DesignSystemPage.tsx`'s collection-layout demo, so this is convergence on
// an existing convention, not a new number). It is OPT-IN (`truncate`
// prop), not a blanket rule, because a short/bounded column (a status chip,
// an id, a date) truncating would be pointless — the story's own text names
// this as the reason a per-column prop beats a global CSS change.
//
// The cap is applied to an INNER wrapper `<span>`, never to the `<td>`/`<th>`
// itself — verified live (Playwright, isolated stack) that `max-width`
// directly on a table CELL is not reliable: a 2-column table with a lot of
// leftover width let Chromium's auto-layout surplus-distribution hand the
// unbounded column extra width ANYWAY, so the cell rendered wider than its
// own `max-width` and nothing truncated. A block-level inner `<span>`
// establishes its OWN box — its `max-width` caps its intrinsic size, which
// is what the table's auto-layout algorithm then reads back as the column's
// max-content contribution — so the column itself, not just the visible
// text, stays bounded regardless of how much spare width the table has.
// `title` is NOT re-implemented here: `TdHTMLAttributes`/`ThHTMLAttributes`
// already carry a native `title`, so it passes straight through `...rest` —
// the caller supplies the cell's own full text (`<TD truncate
// title={value}>`) rather than this primitive trying to derive it from
// arbitrary JSX children. A native `title` alone is a weak a11y pattern on
// its own (not reliably exposed to all AT), so pair it with visible full
// text elsewhere when the value is safety- or task-critical — for a plain
// identity column (a name, an email) the ellipsis + hover/focus tooltip is
// the accepted enterprise-table pattern and is what this primitive
// provides. `TH` gets the identical prop (not just `TD`) so a header can
// share its column's width ceiling — most headers are short static labels
// and never need it, but the column-width CONTRACT is symmetric between a
// column's header and its body cells, so the capability is too.
const TRUNCATE_WRAP_CLASS = 'block max-w-xs truncate';

export interface THProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortDirection?: 'default' | 'ascending' | 'descending';
  onSort?: () => void;
  truncate?: boolean;
}

export function TH({ className = '', children, sortDirection, onSort, truncate = false, ...rest }: THProps): JSX.Element {
  if (!onSort) {
    return (
      <th
        scope="col"
        className={`px-4 py-3 text-label font-semibold uppercase tracking-wide text-muted-foreground ${className}`}
        {...rest}
      >
        {truncate ? <span className={TRUNCATE_WRAP_CLASS}>{children}</span> : children}
      </th>
    );
  }

  const Icon =
    sortDirection === 'ascending' ? ChevronUpIcon : sortDirection === 'descending' ? ChevronDownIcon : ChevronUpDownIcon;

  return (
    <th
      scope="col"
      aria-sort={sortDirection === 'default' || !sortDirection ? 'none' : sortDirection}
      className={`px-4 py-3 text-label font-semibold uppercase tracking-wide text-muted-foreground ${className}`}
      {...rest}
    >
      <button type="button" onClick={onSort} className={`focus-ring flex items-center gap-1 ${truncate ? 'min-w-0' : ''}`}>
        <span className={truncate ? TRUNCATE_WRAP_CLASS : ''}>{children}</span>
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      </button>
    </th>
  );
}

export interface TDProps extends TdHTMLAttributes<HTMLTableCellElement> {
  truncate?: boolean;
}

export function TD({ className = '', children, truncate = false, ...rest }: TDProps): JSX.Element {
  return (
    <td className={`px-4 py-3 text-foreground ${className}`} {...rest}>
      {truncate ? <span className={TRUNCATE_WRAP_CLASS}>{children}</span> : children}
    </td>
  );
}
